/**
 * React Flow specific transformation utilities
 */
import { logger } from '@/utils';
import type { Edge, Node } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import dagre from 'dagre';

import {
  type PipelineBlock,
  PipelineBlockSchema,
  type PipelineConnection,
} from '@mai/types';

import {
  getBlockCategory,
  getReactFlowNodeType,
  getSupportedBlockTypes,
} from '../constants/nodeTypes';
import { createHandleId, parseHandleId } from './handle-ids';

// Helper function to generate type descriptions
export const getTypeDescription = (
  value: unknown,
  maxLength: number = 50,
): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const type = typeof value;

  if (type === 'string') return 'string';
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'function') return 'function';
  if (type === 'symbol') return 'symbol';
  if (type === 'bigint') return 'bigint';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'list()';

    // Get the type of the first element to infer array type
    const firstElementType = getTypeDescription(value[0], 20);
    return `list(${firstElementType})`;
  }

  if (type === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) return 'object({})';

    // Build object type description
    const obj = value as Record<string, unknown>;
    const keyDescriptions: string[] = [];
    let currentLength = 8; // Start with 'object()'

    for (const key of keys) {
      const keyType = getTypeDescription(obj[key], 15);
      const keyDesc = `"${key}": ${keyType}`;

      // Check if adding this key would exceed max length
      if (currentLength + keyDesc.length + 2 > maxLength) {
        // +2 for ', '
        keyDescriptions.push('...');
        break;
      }

      keyDescriptions.push(keyDesc);
      currentLength += keyDesc.length + 2;
    }

    const content = keyDescriptions.join(', ');
    return `object({${content}})`;
  }

  return type;
};

export interface Position {
  x: number;
  y: number;
}

/**
 * Convert a pipeline block to a React Flow node
 */
export function createNodeFromBlock(
  block: PipelineBlock,
  position: Position,
): Node {
  // Use centralized node type classification

  // Generate a human-readable label for the node
  const generateLabel = (block: PipelineBlock): string => {
    // If block has a custom name, use it
    if (block.name) {
      return block.name;
    }
    
    // Otherwise, generate default label based on block type
    switch (block.type) {
      // IO Blocks
      case 'input': {
        return `Input`;
      }
      case 'output':
        return `Output: ${block.config.name || 'result'}`;
      case 'constant': {
        return `Constant`;
      }
      case 'dataset':
        return `Dataset: ${block.config.dataset_id || 'unknown'}`;
      case 'file':
        return `File: ${block.config.file_id || 'unknown'}`;
      case 'artifact':
        return `Artifact: ${block.config.name || 'artifact'}`;

      // Flow Blocks
      case 'identity':
        return 'Identity';
      case 'zip':
        return `Zip (${block.config.combine_strategy || 'merge'})`;
      case 'map':
        return `Map: ${block.config.target_block_type || 'unknown'}`;
      case 'join':
        return `Join (${(block.config.joins as unknown[])?.length || 0} ops)`;

      // AI Blocks
      case 'llm':
        return `LLM (${block.config.provider || 'default'})`;
      case 'embeddings':
        return `Embeddings (${block.config.model?.split('/').pop() || 'default'})`;
      case 'semantic_filter':
        return `Semantic Filter (top ${block.config.top_k || 10})`;
      case 'clause_extraction_agent':
        return 'Clause Extraction Agent';
      case 'legal_variance_determination':
        return 'Legal Variance Determination';

      // Transform Blocks
      case 'string_template':
        return 'String Template';
      case 'json_template':
        return 'JSON Template';
      case 'text_splitter':
        return `Text Splitter (${block.config.chunk_size || 1000})`;
      case 'transform':
        return `Transform (${(block.config.operations as unknown[])?.length || 0} ops)`;
      case 'pdf_reader':
        return 'PDF Reader';
    }
    logger.error(
      `Unknown block type: ${(block as { type: string }).type as string}`,
    );
    return 'Unknown';
  };

  return {
    id: block.id,
    type: getReactFlowNodeType(block.type),
    position,
    dragHandle: '.drag-handle', // Only allow dragging via the drag handle
    data: {
      label: generateLabel(block),
      blockType: block.type,
      category: getBlockCategory(block.type),
      config: block.config,
      name: block.name,
      description: block.description,
      position: position, // Store position in data for persistence
    },
  };
}

/**
 * Convert a pipeline connection to a React Flow edge
 */
export function createEdgeFromConnection(connection: PipelineConnection): Edge {
  return {
    id: `${connection.source_block_id}-${connection.target_block_id}`,
    source: connection.source_block_id,
    target: connection.target_block_id,
    sourceHandle: createHandleId(
      connection.source_block_id,
      connection.source_port,
    ),
    targetHandle: createHandleId(
      connection.target_block_id,
      connection.target_port,
    ),
    type: 'default', // Default curved edges
    style: {
      strokeWidth: 3,
      stroke: '#64748b',
    },
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 8,
      height: 8,
      color: '#475569',
    },
  };
}

/**
 * Extract a pipeline block from a React Flow node
 */
export function extractBlockFromNode(node: Node): PipelineBlock {
  if (!node.data || !node.data.blockType || !node.data.config) {
    throw new Error(`Node "${node.id}" missing required data properties`);
  }

  const blockType = node.data.blockType as PipelineBlock['type'];

  // Validate block type
  const supportedTypes = getSupportedBlockTypes();
  if (!supportedTypes.includes(blockType)) {
    throw new Error(`Unsupported block type: ${blockType}`);
  }

  const blockData: {
    id: string;
    type: PipelineBlock['type'];
    config: PipelineBlock['config'];
    name?: string;
    description?: string;
  } = {
    id: node.id,
    type: blockType,
    config: node.data.config,
  };
  
  if (node.data.name && typeof node.data.name === 'string') {
    blockData.name = node.data.name;
  }
  
  if (node.data.description && typeof node.data.description === 'string') {
    blockData.description = node.data.description;
  }
  
  return PipelineBlockSchema.parse(blockData);
}

/**
 * Extract a pipeline connection from a React Flow edge
 */
export function extractConnectionFromEdge(
  edge: Edge,
  nodes: Node[],
): PipelineConnection {
  // Find source and target nodes to get port information
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  if (!sourceNode) {
    throw new Error(`Source node "${edge.source}" not found`);
  }

  if (!targetNode) {
    throw new Error(`Target node "${edge.target}" not found`);
  }

  // Parse prefixed handle IDs to get port names
  const sourcePort = edge.sourceHandle
    ? parseHandleId(edge.sourceHandle).portName
    : deriveOutputPort(sourceNode);
  const targetPort = edge.targetHandle
    ? parseHandleId(edge.targetHandle).portName
    : deriveInputPort(targetNode);

  return {
    source_block_id: edge.source,
    source_port: sourcePort,
    target_block_id: edge.target,
    target_port: targetPort,
  };
}

/**
 * Derive the output port from a node's block configuration
 */
function deriveOutputPort(node: Node): string {
  const blockType = node.data?.blockType;
  const config = node.data?.config;

  if (!blockType || !config) {
    throw new Error(`Node "${node.id}" missing block type or config`);
  }

  switch (blockType) {
    // IO Blocks
    case 'constant':
      return 'value';
    case 'input':
      return 'data';
    case 'dataset':
      return 'data';
    case 'file':
      return 'data';

    // Flow Blocks
    case 'identity':
      return 'value';
    case 'zip':
      return 'result';
    case 'map':
      return 'result';
    case 'join':
      return 'result';

    // AI Blocks
    case 'llm':
      return 'response';
    case 'embeddings':
      return 'embeddings';
    case 'semantic_filter':
      return 'filtered_results';

    // Transform Blocks
    case 'string_template':
      return 'result';
    case 'text_splitter':
      return 'chunks';
    case 'transform':
      return 'transformed_data';
    case 'pdf_reader':
      return 'markdown_text';

    default:
      throw new Error(`Cannot derive output port for block type: ${blockType}`);
  }
}

/**
 * Derive the input port from a node's block configuration
 */
function deriveInputPort(node: Node): string {
  const blockType = node.data?.blockType;

  if (!blockType) {
    throw new Error(`Node "${node.id}" missing block type`);
  }

  switch (blockType) {
    // IO Blocks
    case 'output':
      return 'data';

    // Flow Blocks
    case 'identity':
      return 'input';
    case 'zip':
      return 'input1'; // First input port for zip blocks
    case 'map':
      return 'data';
    case 'join':
      return 'data';

    // AI Blocks
    case 'llm':
      return 'input';
    case 'embeddings':
      return 'texts';
    case 'semantic_filter':
      return 'query';

    // Transform Blocks
    case 'string_template':
      return 'input';
    case 'text_splitter':
      return 'text';
    case 'transform':
      return 'input';
    case 'pdf_reader':
      return 'file';

    default:
      throw new Error(`Cannot derive input port for block type: ${blockType}`);
  }
}

/**
 * Calculate automatic layout positions for blocks using Dagre algorithm
 */
export function calculateAutoLayout(
  blocks: PipelineBlock[],
  connections: PipelineConnection[],
  useDagre: boolean = true,
): Map<string, Position> {
  // Handle empty blocks array
  if (blocks.length === 0) {
    return new Map();
  }

  // Handle single block case - position it at a reasonable default location
  if (blocks.length === 1) {
    const positions = new Map<string, Position>();
    positions.set(blocks[0].id, { x: 100, y: 100 });
    return positions;
  }

  // For multiple blocks, use the appropriate layout algorithm
  if (useDagre) {
    return calculateDagreLayout(blocks, connections);
  } else {
    return calculateSimpleLayout(blocks, connections);
  }
}

/**
 * Calculate optimal positions using Dagre layout algorithm
 * Provides better handling of complex DAGs with crossing edges and multiple paths
 */
function calculateDagreLayout(
  blocks: PipelineBlock[],
  connections: PipelineConnection[],
): Map<string, Position> {
  try {
    // Handle edge case for empty or single block
    if (blocks.length === 0) {
      return new Map();
    }
    if (blocks.length === 1) {
      const positions = new Map<string, Position>();
      positions.set(blocks[0].id, { x: 100, y: 100 });
      return positions;
    }

    // Create a new directed graph
    const graph = new dagre.graphlib.Graph();

    // Adjust spacing based on number of blocks for better linear layout
    const isSimpleLinear =
      blocks.length <= 5 && connections.length === blocks.length - 1;
    const ranksep = isSimpleLinear ? 180 : 200; // Tighter spacing for simple linear pipelines

    // Set graph configuration for pipeline layout
    graph.setGraph({
      rankdir: 'LR', // Left to Right (good for pipelines)
      nodesep: 80, // Spacing between nodes on same level
      ranksep, // Dynamic spacing between levels
      marginx: 20, // Reduced margin X
      marginy: 20, // Reduced margin Y
      align: 'UL', // Align to upper left for consistent positioning
    });

    // Default edge configuration
    graph.setDefaultEdgeLabel(() => ({}));

    // Add nodes to the graph
    blocks.forEach((block) => {
      graph.setNode(block.id, {
        width: 200, // Updated to match new node width
        height: 80, // Updated height for larger nodes
        label: block.id,
      });
    });

    // Add edges to the graph
    connections.forEach((connection) => {
      graph.setEdge(connection.source_block_id, connection.target_block_id, {
        label: `${connection.source_port} → ${connection.target_port}`,
      });
    });

    // Run the layout algorithm
    dagre.layout(graph);

    // Extract positions
    const positions = new Map<string, Position>();

    blocks.forEach((block) => {
      const node = graph.node(block.id);
      if (node) {
        positions.set(block.id, {
          x: node.x - node.width / 2, // Dagre uses center positioning, React Flow uses top-left
          y: node.y - node.height / 2,
        });
      }
    });

    return positions;
  } catch (error) {
    // Fallback to simple layout if dagre fails
    logger.warn('Dagre layout failed, falling back to simple layout:', error);
    return calculateSimpleLayout(blocks, connections);
  }
}

/**
 * Simple fallback layout algorithm (original implementation)
 */
function calculateSimpleLayout(
  blocks: PipelineBlock[],
  connections: PipelineConnection[],
): Map<string, Position> {
  const positions = new Map<string, Position>();

  // Handle edge case for empty or single block
  if (blocks.length === 0) {
    return positions;
  }
  if (blocks.length === 1) {
    positions.set(blocks[0].id, { x: 100, y: 100 });
    return positions;
  }

  // Constants for layout - increased for larger nodes
  const HORIZONTAL_SPACING = 250;
  const VERTICAL_SPACING = 140;
  const START_X = 0;
  const START_Y = 0;

  // Build dependency graph
  const incomingConnections = new Map<string, PipelineConnection[]>();
  const outgoingConnections = new Map<string, PipelineConnection[]>();

  for (const connection of connections) {
    // Track incoming connections to each block
    if (!incomingConnections.has(connection.target_block_id)) {
      incomingConnections.set(connection.target_block_id, []);
    }
    incomingConnections.get(connection.target_block_id)!.push(connection);

    // Track outgoing connections from each block
    if (!outgoingConnections.has(connection.source_block_id)) {
      outgoingConnections.set(connection.source_block_id, []);
    }
    outgoingConnections.get(connection.source_block_id)!.push(connection);
  }

  // Categorize blocks by type and dependencies
  const inputBlocks: PipelineBlock[] = [];
  const outputBlocks: PipelineBlock[] = [];
  const processingBlocks: PipelineBlock[] = [];

  for (const block of blocks) {
    if (block.type === 'input' || block.type === 'constant') {
      inputBlocks.push(block);
    } else if (block.type === 'output') {
      outputBlocks.push(block);
    } else {
      processingBlocks.push(block);
    }
  }

  // Calculate depth for each block (distance from inputs)
  const depths = calculateBlockDepths(blocks, connections, incomingConnections);

  // Position input blocks on the left
  inputBlocks.forEach((block, index) => {
    positions.set(block.id, {
      x: START_X,
      y: START_Y + index * VERTICAL_SPACING,
    });
  });

  // Position processing blocks by depth
  const processingByDepth = new Map<number, PipelineBlock[]>();
  for (const block of processingBlocks) {
    const depth = depths.get(block.id) || 1;
    if (!processingByDepth.has(depth)) {
      processingByDepth.set(depth, []);
    }
    processingByDepth.get(depth)!.push(block);
  }

  // Layout processing blocks by depth
  for (const [depth, blocksAtDepth] of processingByDepth) {
    blocksAtDepth.forEach((block, index) => {
      positions.set(block.id, {
        x: START_X + depth * HORIZONTAL_SPACING,
        y: START_Y + index * VERTICAL_SPACING,
      });
    });
  }

  // Position output blocks on the right
  const maxDepth = Math.max(...Array.from(depths.values()), 0);
  const outputX = START_X + (maxDepth + 1) * HORIZONTAL_SPACING;

  outputBlocks.forEach((block, index) => {
    positions.set(block.id, {
      x: outputX,
      y: START_Y + index * VERTICAL_SPACING,
    });
  });

  // Handle any blocks not yet positioned (isolated blocks)
  for (const block of blocks) {
    if (!positions.has(block.id)) {
      positions.set(block.id, {
        x: START_X,
        y: START_Y + positions.size * VERTICAL_SPACING,
      });
    }
  }

  return positions;
}

/**
 * Calculate the depth (distance from inputs) for each block
 */
function calculateBlockDepths(
  blocks: PipelineBlock[],
  connections: PipelineConnection[],
  incomingConnections: Map<string, PipelineConnection[]>,
): Map<string, number> {
  const depths = new Map<string, number>();
  const visited = new Set<string>();

  // Find all blocks with no incoming connections (inputs/constants)
  const rootBlocks = blocks.filter(
    (block) =>
      block.type === 'input' ||
      block.type === 'constant' ||
      !incomingConnections.has(block.id),
  );

  // Set depth 0 for root blocks
  for (const block of rootBlocks) {
    depths.set(block.id, 0);
  }

  // Build outgoing connections map
  const outgoing = new Map<string, string[]>();
  for (const connection of connections) {
    if (!outgoing.has(connection.source_block_id)) {
      outgoing.set(connection.source_block_id, []);
    }
    outgoing.get(connection.source_block_id)!.push(connection.target_block_id);
  }

  // BFS to calculate depths
  const queue = [...rootBlocks.map((b) => b.id)];

  while (queue.length > 0) {
    const blockId = queue.shift()!;

    if (visited.has(blockId)) {
      continue;
    }
    visited.add(blockId);

    const currentDepth = depths.get(blockId) || 0;
    const targetBlocks = outgoing.get(blockId) || [];

    for (const targetId of targetBlocks) {
      const newDepth = currentDepth + 1;
      const existingDepth = depths.get(targetId);

      if (existingDepth === undefined || newDepth > existingDepth) {
        depths.set(targetId, newDepth);
        queue.push(targetId);
      }
    }
  }

  return depths;
}

/**
 * Optimize layout to minimize edge crossings and improve readability
 */
export function optimizeLayout(
  positions: Map<string, Position>,
): Map<string, Position> {
  // For now, return positions as-is
  // Future enhancements could include:
  // - Edge crossing minimization
  // - Compact layout algorithms
  // - User preference-based positioning
  return new Map(positions);
}

/**
 * Validate that all blocks can be properly positioned
 */
export function validateLayoutInputs(
  blocks: PipelineBlock[],
  connections: PipelineConnection[],
): { valid: boolean; error?: string } {
  // Check for duplicate block IDs
  const blockIds = new Set<string>();
  for (const block of blocks) {
    if (blockIds.has(block.id)) {
      return {
        valid: false,
        error: `Duplicate block ID: ${block.id}`,
      };
    }
    blockIds.add(block.id);
  }

  // Check that all connections reference valid blocks
  for (const connection of connections) {
    if (!blockIds.has(connection.source_block_id)) {
      return {
        valid: false,
        error: `Connection references non-existent source block: ${connection.source_block_id}`,
      };
    }

    if (!blockIds.has(connection.target_block_id)) {
      return {
        valid: false,
        error: `Connection references non-existent target block: ${connection.target_block_id}`,
      };
    }
  }

  return { valid: true };
}
